import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SoarQueryKeys } from '../queryKeys';
import { PlaybookDto, playbookService } from '../services/playbookService';
import { Playbook } from '../types';

export interface UsePlaybooksOptions {
  enabled?: boolean;
}

export const usePlaybooks = (options: UsePlaybooksOptions = {}) => {
  const queryClient = useQueryClient();
  const enabled = options.enabled ?? Object.keys(options).length === 0;

  const mapDtoToPlaybook = useCallback(
    (dto: PlaybookDto): Playbook => ({
      id: dto.playbookId,
      name: dto.name,
      description: dto.description,
      entityType: dto.entityType,
      owner: dto.createdByUserId || 'System',
      lastUpdate: dto.updatedAt || dto.createdAt || new Date().toISOString(),
      runs: dto.runs || 0,
      failedSuccess: `${dto.failedCount || 0}/${dto.successCount || 0}`,
      status: dto.enabled ? 'Active' : 'Inactive',
      actions: dto.actions,
    }),
    []
  );

  const {
    data: playbooks = [],
    isLoading,
    isFetched,
    refetch: fetchPlaybooks,
  } = useQuery({
    queryKey: [SoarQueryKeys.PLAYBOOKS],
    queryFn: async () => {
      const data = await playbookService.getPlaybooks();
      return Array.isArray(data) ? data.map(mapDtoToPlaybook) : [];
    },
    enabled,
  });

  const deletePlaybookMutation = useMutation({
    mutationFn: (id: string) => playbookService.deletePlaybook(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SoarQueryKeys.PLAYBOOKS] });
    },
  });

  const addPlaybook = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [SoarQueryKeys.PLAYBOOKS] });
  }, [queryClient]);

  const updatePlaybook = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [SoarQueryKeys.PLAYBOOKS] });
  }, [queryClient]);

  return {
    playbooks,
    isLoading,
    error: null, // useQuery has error, but keeping it null for compatibility if not used
    isFetched,
    fetchPlaybooks,
    deletePlaybook: (id: string) => deletePlaybookMutation.mutate(id),
    addPlaybook,
    updatePlaybook,
  };
};
